import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '../utils/toast';
import axios from 'axios';
import { sortObjectOptions } from '@/utils/sortOptions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TopicAssignmentPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [jobFunctions, setJobFunctions] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [assignmentType, setAssignmentType] = useState('all');
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [topicsRes, deptRes, jobRes] = await Promise.all([
        axios.get(`${API}/training/content`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hospital/departments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hospital/job-functions`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTopics(topicsRes.data);
      setDepartments(deptRes.data.departments);
      setJobFunctions(jobRes.data.job_functions);
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };
  
  const handleAssign = async () => {
    if (selectedTopics.length === 0) {
      toast.error('Please select at least one topic');
      return;
    }
    
    if (assignmentType === 'department' && !selectedDept) {
      toast.error('Please select a department');
      return;
    }
    
    if (assignmentType === 'job_function' && !selectedJob) {
      toast.error('Please select a job role');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/hospital/topics/assign`,
        {
          training_topic_ids: selectedTopics,
          assignment_type: assignmentType,
          department_id: assignmentType === 'department' ? selectedDept : null,
          job_function_id: assignmentType === 'job_function' ? selectedJob : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Topics assigned successfully');
      setSelectedTopics([]);
      setAssignmentType('all');
      setSelectedDept('');
      setSelectedJob('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign chapters');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <AdminLayout>
      <div className="p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">TOPIC ASSIGNMENT</h1>
        
        <div className="max-w-4xl">
          <div className="bg-white rounded-xl shadow-md p-6 space-y-6">
          <div>
            <Label>Assignment Type *</Label>
            <Select value={assignmentType} onValueChange={setAssignmentType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff (Institution-wide)</SelectItem>
                <SelectItem value="department">By Department</SelectItem>
                <SelectItem value="job_function">By Job Role</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {assignmentType === 'department' && (
            <div>
              <Label>Select Department *</Label>
              <Select value={selectedDept} onValueChange={setSelectedDept}>
                <SelectTrigger><SelectValue placeholder="Choose department" /></SelectTrigger>
                <SelectContent>
                  {sortObjectOptions(departments, 'department_name').map(d => <SelectItem key={d.department_id} value={d.department_id}>{d.department_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {assignmentType === 'job_function' && (
            <div>
              <Label>Select Job Role *</Label>
              <Select value={selectedJob} onValueChange={setSelectedJob}>
                <SelectTrigger><SelectValue placeholder="Choose job role" /></SelectTrigger>
                <SelectContent>
                  {sortObjectOptions(jobFunctions, 'job_function_name').map(j => <SelectItem key={j.job_function_id} value={j.job_function_id}>{j.job_function_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="mb-3 block">Select Topics to Assign *</Label>
            <div className="max-h-96 overflow-y-auto space-y-2 border rounded-lg p-4">
              {topics.map(topic => (
                <div key={topic.id} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded">
                  <Checkbox
                    checked={selectedTopics.includes(topic.id)}
                    onCheckedChange={(checked) => {
                      if (checked) setSelectedTopics([...selectedTopics, topic.id]);
                      else setSelectedTopics(selectedTopics.filter(id => id !== topic.id));
                    }}
                  />
                  <div>
                    <p className="font-medium">{topic.title}</p>
                    <p className="text-sm text-gray-600">{topic.description || 'No description'}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Button onClick={handleAssign} className="w-full bg-[#0056B3]" disabled={loading}>
            {loading ? 'Assigning...' : `Assign ${selectedTopics.length} Topics`}
          </Button>
        </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default TopicAssignmentPage;